import { Unibabel } from 'unibabel';
import { Buffer } from 'buffer';
const algorithmRSA = {
	name: 'RSA-OAEP',
	modulusLength: 2048,
	publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
	hash: { name: 'SHA-256' },
};

const algorithmAES = {
	name: 'AES-CBC',
	length: 256,
};

const algorithmAESGCM = {
	name: 'AES-GCM',
	length: 256,
};

const IV_LENGTH_BYTES = 12;

export class CryptoProtocolError extends Error {
	constructor(code, cause) {
		super(`Crypto protocol error: ${code}`);
		this.name = 'CryptoProtocolError';
		this.code = code;
		this.cause = cause;
	}
}

export const generateKeyPair = async () => {
	const keyPair = await crypto.subtle.generateKey(algorithmRSA, true, ['encrypt', 'decrypt']);
	const publicKey = await crypto.subtle.exportKey('spki', keyPair.publicKey);
	const base64PublicKey = Unibabel.bufferToBase64(new Uint8Array(publicKey));

	return {
		privateKey: keyPair.privateKey,
		publicKey: base64PublicKey,
	};
};

export const getRSAKey = async (rawPublicKey) =>
	crypto.subtle.importKey('spki', Unibabel.base64ToArr(rawPublicKey).buffer, algorithmRSA, false, [
		'encrypt',
	]);

export const generateSeed = () => {
	const rawSeed = window.crypto.getRandomValues(new Uint8Array(16));
	const token = Buffer.from(rawSeed).toString('base64');

	return token;
};

export const generateAESKey = async () => {
	const key = await crypto.subtle.generateKey(algorithmAES, true, ['encrypt', 'decrypt']);
	const exportedKey = await crypto.subtle.exportKey('raw', key);
	const seed = generateSeed();
	const symmetricKey = Buffer.from(exportedKey).toString('base64');

	return {
		key,
		symmetricKey,
		seed,
	};
};

export const getAESKey = async (base64String) => {
	const symmetricKeyBuffer = Unibabel.base64ToArr(base64String).buffer;
	const key = await crypto.subtle.importKey(
		'raw',
		symmetricKeyBuffer,
		{ name: algorithmAES.name },
		true,
		['encrypt', 'decrypt']
	);
	return key;
};

export const decryptRSA = async (privateKey, data) => {
	const response = await crypto.subtle.decrypt(
		algorithmRSA,
		privateKey,
		Unibabel.base64ToArr(data)
	);
	return JSON.parse(Unibabel.bufferToUtf8(new Uint8Array(response)));
};

export const encryptRSA = async (publicKey, data) => {
	const response = await crypto.subtle.encrypt(
		algorithmRSA,
		publicKey,
		Unibabel.utf8ToBuffer(JSON.stringify(data)).buffer
	);

	return Buffer.from(response).toString('base64');
};

export const encryptAES = async ({ seed, key, data }) => {
	const seedBuffer = Unibabel.base64ToArr(seed).buffer;
	const encryptedData = await crypto.subtle.encrypt(
		{
			name: algorithmAES.name,
			iv: seedBuffer,
		},
		key,
		Unibabel.utf8ToBuffer(JSON.stringify(data)).buffer
	);
	try {
		// eslint-disable-next-line no-unused-vars
		const encryptedDataString = Buffer.from(encryptedData).toString('base64');
	} catch {
		/* empty */
	}
	return Buffer.from(encryptedData).toString('base64');
};

export const decryptAES = async ({ seed, key, data }) => {
	const seedBuffer = Unibabel.base64ToArr(seed).buffer;

	const response = await crypto.subtle.decrypt(
		{
			name: algorithmAES.name,
			iv: seedBuffer,
		},
		key,
		Unibabel.base64ToBuffer(data).buffer
	);

	return JSON.parse(Unibabel.bufferToUtf8(new Uint8Array(response)));
};

// Returns a random 12-byte IV (AES-GCM requirement) encoded in base64
export const generateIV = () => {
	const ivBytes = crypto.getRandomValues(new Uint8Array(IV_LENGTH_BYTES));

	return Buffer.from(ivBytes).toString('base64');
};

const decodeIV = (iv) => {
	let ivBuffer;
	try {
		ivBuffer = Unibabel.base64ToArr(iv).buffer;
	} catch (err) {
		throw new CryptoProtocolError('INVALID_IV_BASE64', err);
	}
	if (ivBuffer.byteLength !== IV_LENGTH_BYTES) {
		throw new CryptoProtocolError('INVALID_IV_BASE64');
	}

	return ivBuffer;
};

export const getAESKeyGCM = async (base64String) => {
	const symmetricKeyBuffer = Unibabel.base64ToArr(base64String).buffer;
	const key = await crypto.subtle.importKey(
		'raw',
		symmetricKeyBuffer,
		{ name: algorithmAESGCM.name },
		true,
		['encrypt', 'decrypt']
	);
	return key;
};

// Encrypts data with AES-GCM (v2). The 128-bit authentication tag is appended automatically
export const encryptAESGCM = async ({ iv, key, data }) => {
	const ivBuffer = decodeIV(iv);
	const encryptedData = await crypto.subtle.encrypt(
		{
			name: algorithmAESGCM.name,
			iv: ivBuffer,
		},
		key,
		Unibabel.utf8ToBuffer(JSON.stringify(data)).buffer
	);

	return Buffer.from(encryptedData).toString('base64');
};

// Decrypts a base64 string (ciphertext + tag) with AES-GCM (v2)
export const decryptAESGCM = async ({ iv, key, data }) => {
	const ivBuffer = decodeIV(iv);
	let response;
	try {
		response = await crypto.subtle.decrypt(
			{
				name: algorithmAESGCM.name,
				iv: ivBuffer,
			},
			key,
			Unibabel.base64ToBuffer(data).buffer
		);
	} catch (err) {
		throw new CryptoProtocolError('GCM_TAG_MISMATCH', err);
	}

	return JSON.parse(Unibabel.bufferToUtf8(new Uint8Array(response)));
};

/* istanbul ignore next */
export const cypherSha256 = async (message) => {
	const msgUint8 = Unibabel.utf8ToBuffer(message).buffer;
	const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
	return Buffer.from(hashBuffer).toString('base64');
};

/* istanbul ignore next */
export const stringToHash = function (str, seed = 0) {
	let h1 = 0xdeadbeef ^ seed;
	let h2 = 0x41c6ce57 ^ seed;
	for (let i = 0, ch; i < str.length; i += 1) {
		ch = str.charCodeAt(i);
		h1 = Math.imul(h1 ^ ch, 2654435761);
		h2 = Math.imul(h2 ^ ch, 1597334677);
	}
	h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
	h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
	return (h2 >>> 0).toString(16).padStart(8, 0) + (h1 >>> 0).toString(16).padStart(8, 0);
};
